<script setup>
import { computed, ref, onMounted, onUnmounted } from "vue";
import { useMainStore } from "@/stores/store.js";
import {
	PhX,
	PhPlus,
	PhCaretUp,
	PhCaretDown,
	PhWarning,
	PhCheckCircle,
	PhXCircle,
	PhArrowCounterClockwise,
	PhArrowUUpLeft,
	PhMagicWand,
} from "@phosphor-icons/vue";
import BaseConfirmDialog from "@/components/utils/BaseConfirmDialog.vue";

const store = useMainStore();

//
// Props
//
const props = defineProps({
	productTooBig: {
		type: Boolean,
		default: false,
	},
});

//
// Emits
//
const emit = defineEmits(["close", "add-product"]);

//
// Computed properties (because they are being referenced in the code part of the component)
//
const isTargetMet = computed(() => store.isTargetMet);

//
// Validation state
//
const isReportValid = computed(() => store.stepReportIsValid);
const invalidProducts = computed(() => store.invalidProducts);

//
// Component-specific: depends on props and validation
//
const canGenerateReport = computed(() => {
	return isTargetMet.value && !props.productTooBig && isReportValid.value;
});

// Check if a specific product is valid
const isProductValid = (productId) => {
	return !store.invalidProducts.some((item) => item.productId === productId);
};

// Scroll to product card when product name is clicked
const scrollToProduct = (productId) => {
	const element = document.getElementById(`product-card-${productId}`);
	if (element) {
		// Use matchMedia to check if we're on desktop (md breakpoint and up)
		// Only apply offset on desktop where header is fixed
		const mediaQuery = window.matchMedia("(min-width: 768px)");
		const offset = mediaQuery.matches ? 120 : 20;

		const elementPosition = element.getBoundingClientRect().top;
		const offsetPosition = elementPosition + window.pageYOffset - offset;

		window.scrollTo({
			top: offsetPosition,
			behavior: "smooth",
		});

		// // Add a subtle highlight effect
		// element.classList.add('highlight-product');
		// setTimeout(() => {
		// 	element.classList.remove('highlight-product');
		// }, 2000);
	}
};

// Product removal state and handlers
const productToRemove = ref(null);

// Reset products confirmation state
const showResetConfirmation = ref(false);

//
// Panel toggle state for mobile
//
const isExpanded = ref(true);

//
// Footer height tracking for mobile collapse positioning
//
const footerHeight = ref(0);
let footerResizeObserver = null;

const updateFooterHeight = () => {
	const footer = document.getElementById("luxxbox-acoustic-analyzer-footer");
	if (footer) {
		const rect = footer.getBoundingClientRect();
		// Calculate distance from viewport bottom to footer's top edge
		// This accounts for any spacing below the footer (browser chrome, etc.)
		footerHeight.value = window.innerHeight - rect.top;
	} else {
		footerHeight.value = 0;
	}
};

// Computed style for panel positioning
// Panel always sits above the footer, then translates down when collapsed
const panelStyle = computed(() => {
	const baseStyle = {};

	// Always position panel above the footer on mobile
	if (footerHeight.value > 0) {
		baseStyle.bottom = `${footerHeight.value}px`;
	}

	// When collapsed, translate down by 100% to hide panel (toggle handle stays visible)
	if (!isExpanded.value) {
		baseStyle.transform = "translateY(100%)";
	}

	return baseStyle;
});

onMounted(() => {
	// Initial measurement
	updateFooterHeight();

	// Watch for footer size changes
	const footer = document.getElementById("luxxbox-acoustic-analyzer-footer");
	if (footer && typeof ResizeObserver !== "undefined") {
		footerResizeObserver = new ResizeObserver(() => {
			updateFooterHeight();
		});
		footerResizeObserver.observe(footer);
	}

	// Also listen for window resize as a fallback
	window.addEventListener("resize", updateFooterHeight);
});

onUnmounted(() => {
	if (footerResizeObserver) {
		footerResizeObserver.disconnect();
		footerResizeObserver = null;
	}
	window.removeEventListener("resize", updateFooterHeight);
});

/*
------------------------------------------------------------------------
Handle events
------------------------------------------------------------------------
*/

//
// Toggle panel visibility on mobile
//
const handleTogglePanel = () => {
	isExpanded.value = !isExpanded.value;
};

// Generate report
const handleGenerateReport = () => {
	store.triggerDocGen = true;
};

// Handle remove product (with confirmation)
const handleRemoveProduct = (productId, event) => {
	event.stopPropagation(); // Prevent triggering scroll
	if (!store.isPrimaryProduct(productId)) {
		productToRemove.value = productId;
	}
};

// Confirm removal
const handleConfirmRemoveProduct = async () => {
	if (productToRemove.value) {
		store.removeProduct(productToRemove.value);
		// Trigger recalculation for remaining products
		await store.calculateProductsBatch();
		productToRemove.value = null;
	}
};

// Cancel removal
const handleCancelRemoveProduct = () => {
	productToRemove.value = null;
};

// Handle reset products (with confirmation)
const handleResetProducts = () => {
	showResetConfirmation.value = true;
};

// Confirm reset products
const handleConfirmResetProducts = async () => {
	await store.resetProducts();
	showResetConfirmation.value = false;
};

// Cancel reset products
const handleCancelResetProducts = () => {
	showResetConfirmation.value = false;
};
</script>

<template>
	<div
		class="fixed left-0 right-0 bg-navoverlay px-4 pb-0 pt-4 transition-transform duration-300 ease-in-out focus:outline-none focus:ring-0 focus:ring-offset-0 md:sticky md:bottom-auto md:left-auto md:right-auto md:top-[146px] md:mx-auto md:bg-navoverlay md:p-2"
		:style="panelStyle"
	>
		<button
			type="button"
			@click="handleTogglePanel"
			class="toggle-handle absolute right-0 cursor-pointer p-4 py-3 transition-colors focus:outline-none focus:ring-0 focus:ring-offset-0 md:hidden"
			:class="
				isReportValid && isTargetMet
					? 'bg-accent text-white hover:bg-accentdark'
					: 'bg-red-300 text-black'
			"
			aria-label="Toggle results panel"
		>
			<PhCaretDown v-if="isExpanded" :size="16" weight="bold" />
			<PhCaretUp v-else :size="16" weight="bold" />
		</button>

		<!-- Header -->
		<!-- <button type="button" class="w-full bg-black text-white px-4 py-2.5 flex items-center justify-between">
			<span class="text-base font-semibold tracking-[0.8px] uppercase"> RESULTS </span>
		</button> -->

		<!--
		Report Validation Status
		-->
		<section
			v-if="!isReportValid"
			class="mb-1 flex flex-row items-center gap-1 bg-red-100 px-4 py-1.5 text-sm font-bold text-red-800"
		>
			<PhWarning size="14" weight="bold" />
			<p>Complete all required fields.</p>
		</section>

		<!-- Panel Content (collapsible on mobile) -->
		<div class="flex flex-col gap-2">
			<!--
			Selected Products Section
			-->
			<section
				class="flex-col bg-white py-2 text-black md:flex md:gap-0 mb-1 md:mb-0"
			>
				<!--
				Selected Products Header
				-->
				<div class="flex items-center justify-between px-4 py-1.5 bg-green">
					<div
						class="flex flex-row items-center gap-1 text-sm font-bold uppercase md:text-base"
					>
						<span class="block md:hidden">{{
							store.user.products.length
						}}</span>
						Products
						<div
							v-if="store.hasCustomProductSelections"
							@click="handleResetProducts"
							class="flex lux-focus-ring h-5 w-5 flex-shrink-0 cursor-pointer items-center justify-center hover:bg-accent hover:text-white ml-1"
							title="Reset products"
						>
							<PhArrowUUpLeft
								size="16"
								weight="bold"
								class="w-3.5 h-3.5 md:w-4 md:h-4"
							/>
						</div>
					</div>
					<div
						@click="emit('add-product')"
						class="lux-focus-ring flex h-5 w-5 flex-shrink-0 cursor-pointer items-center justify-center hover:bg-accent hover:text-white"
						title="Add another product"
					>
						<PhPlus size="16" weight="bold" class="w-3.5 h-3.5 md:w-4 md:h-4" />
					</div>
				</div>

				<!--
				Report Validation Status
				-->
				<div
					v-if="!isReportValid"
					class="mx-4 hidden bg-red-100 px-2 py-1.5 text-sm text-red-800"
				>
					<p>Please complete all required fields</p>
				</div>

				<!--
				Selected Products List
				-->
				<div
					v-for="product in store.user.products || []"
					:key="product.id"
					class="hidden md:flex flex-row items-center px-4 hover:bg-gray-100"
				>
					<button
						type="button"
						@click="scrollToProduct(product.id)"
						class="flex w-full cursor-pointer gap-1 py-1.5 text-left text-sm font-semibold transition-colors md:text-base"
						:class="{
							'': isProductValid(product.id),
							'text-red-400 md:text-red-600': !isProductValid(product.id),
						}"
					>
						<div class="flex-0 flex flex-row gap-1 text-sm">
							<div class="flex-0 text-left">
								{{ store.getProductQuantity(product) }}x
							</div>
							<div class="flex-0">
								{{ product.data?.name || "Product" }}
							</div>
							<div class="flex-0">
								({{
									product.size_variant
										? `${product.size_variant}`
										: `${product.size}`
								}})
							</div>
						</div>
					</button>
					<!-- Validation warning icon for invalid products -->
					<!-- <span v-if="!isProductValid(product.id)" class="flex items-center text-red-500" title="Missing required fields"><PhWarning size="16" weight="bold" /> </span> -->
					<button
						v-if="!store.isPrimaryProduct(product.id)"
						type="button"
						id="modal_close"
						@click="handleRemoveProduct(product.id, $event)"
						title="Remove this product"
						class="spin-effect lux-focus-ring flex h-5 w-5 flex-shrink-0 items-center justify-center text-gray-600 transition-colors hover:text-red-600"
					>
						<PhX
							size="16"
							aria-hidden="true"
							weight="bold"
							class="lux-pos-absmiddle text-blacka75 hover:text-red-600"
							title="Remove product"
						/>
					</button>
				</div>
			</section>

			<!-- Starting Section (Gray) -->
			<section class="hidden md:hidden">
				<div
					class="results-section-gray hidden flex-col gap-0.5 border-t px-5 py-2.5 md:block"
				>
					<div class="flex items-center justify-between">
						<span class="flex-1 text-sm font-semibold leading-6 text-black">
							STARTING T60
						</span>
						<span
							class="text-center text-sm font-semibold leading-6 text-black"
						>
							{{ store.startingT60 }}
						</span>
					</div>
					<div class="flex items-center justify-between">
						<span class="flex-1 text-sm font-semibold leading-6 text-black">
							STARTING SABINS
						</span>
						<span
							class="text-center text-sm font-semibold leading-6 text-black"
						>
							{{ store.startingSabins }}
						</span>
					</div>
				</div>

				<!-- Target Section (Gray) -->
				<div
					class="results-section-gray hidden flex-col gap-0.5 border-t px-5 py-2.5 md:block"
				>
					<div class="flex items-center justify-between">
						<span class="flex-1 text-sm font-semibold leading-6 text-black">
							TARGET T60
						</span>
						<span
							class="text-center text-sm font-semibold leading-6 text-black"
						>
							{{ store.targetT60 }}
						</span>
					</div>
					<div class="flex items-center justify-between">
						<span class="flex-1 text-sm font-semibold leading-6 text-black">
							TARGET SABINS
						</span>
						<span
							class="text-center text-sm font-semibold leading-6 text-black"
						>
							{{ store.targetSabins }}
						</span>
					</div>
				</div>

				<!-- Products Section (Gray) -->
				<div
					class="results-section-gray hidden flex-col gap-0.5 border-t px-5 py-2.5 md:block"
				>
					<div class="flex items-center justify-between">
						<span class="flex-1 text-sm font-semibold leading-6 text-black">
							PRODUCTS T60
						</span>
						<span
							class="text-center text-sm font-semibold leading-6 text-black"
						>
							{{ store.productsT60 }}
						</span>
					</div>
					<div class="flex items-center justify-between">
						<span class="flex-1 text-sm font-semibold leading-6 text-black">
							PRODUCTS SABINS
						</span>
						<span
							class="text-center text-sm font-semibold leading-6 text-black"
						>
							{{ store.productsSabins }}
						</span>
					</div>
				</div>
			</section>

			<!-- Results Section -->
			<section
				class="flex flex-col py-2 text-black"
				:class="isReportValid && isTargetMet ? 'bg-green-100' : 'bg-red-100'"
			>
				<!--
				Results Header
				-->
				<div class="flex items-center justify-between px-4 py-1.5">
					<div
						class="flex flex-row items-center gap-1 text-sm font-bold uppercase md:text-base"
						:class="{
							'text-green-800': isReportValid && isTargetMet,
							'text-red-800': !isReportValid || !isTargetMet,
						}"
					>
						Results
						<PhCheckCircle
							:size="16"
							weight="bold"
							class="text-green-800"
							v-if="isReportValid && isTargetMet"
						/>
						<PhXCircle :size="16" weight="bold" class="text-red-800" v-else />
					</div>
				</div>

				<!-- T60 -->
				<div
					class="flex items-center justify-between px-4 py-1.5 text-sm md:text-lg"
				>
					<span
						class="text-center font-semibold leading-5 tracking-wider"
						:class="
							isReportValid && isTargetMet ? 'text-green-800' : 'text-red-800'
						"
					>
						T60
					</span>
					<div class="flex items-end gap-2">
						<span
							class="font-bold leading-5"
							:class="
								isReportValid && isTargetMet ? 'text-black/40' : 'text-red-800'
							"
						>
							{{ store.t60Delta }}
						</span>
						<span
							class="text-center font-semibold leading-5 tracking-wider"
							:class="
								isReportValid && isTargetMet ? 'text-green-800' : 'text-red-800'
							"
						>
							{{ store.resultingT60 }}
						</span>
					</div>
				</div>

				<!-- SABINS -->
				<div
					class="flex items-center justify-between px-4 py-1.5 text-sm md:text-lg"
				>
					<span
						class="text-center font-semibold leading-5 tracking-wider"
						:class="
							isReportValid && isTargetMet ? 'text-green-800' : 'text-red-800'
						"
					>
						SABINS
					</span>
					<div class="flex items-end gap-2">
						<span
							class="font-bold leading-5"
							:class="
								isReportValid && isTargetMet ? 'text-black/40' : 'text-red-800'
							"
						>
							{{ store.sabinsDelta }}
						</span>
						<span
							class="text-center font-semibold leading-5 tracking-wider"
							:class="
								isReportValid && isTargetMet ? 'text-green-800' : 'text-red-800'
							"
						>
							{{ store.totalSabins }}
						</span>
					</div>
				</div>

				<!-- LUMENS -->
				<div
					class="flex items-center justify-between px-4 py-1.5 text-sm md:text-lg"
				>
					<span
						class="text-center font-semibold leading-5 tracking-wider"
						:class="
							isReportValid && isTargetMet ? 'text-green-800' : 'text-red-800'
						"
					>
						LUMENS
					</span>
					<div class="flex items-end gap-2">
						<span
							class="text-center font-semibold leading-5 tracking-wider"
							:class="
								isReportValid && isTargetMet ? 'text-green-800' : 'text-red-800'
							"
						>
							{{ store.totalLumens }}
						</span>
					</div>
				</div>
			</section>

			<!-- Generate Report Button -->
			<button
				type="button"
				@click="handleGenerateReport"
				:disabled="!canGenerateReport"
				class="hidden w-full flex-row items-center justify-center gap-2 bg-accent px-8 pb-2.5 pt-3 text-center text-base font-semibold uppercase tracking-[1.6px] text-white transition-opacity md:flex md:items-center"
				:class="{
					'cursor-not-allowed opacity-50': !canGenerateReport,
					'cursor-pointer': canGenerateReport,
					'bg-red-300': !isReportValid || !isTargetMet,
					'bg-accent': isReportValid && isTargetMet,
				}"
			>
				<PhMagicWand :size="16" /> GENERATE REPORT
			</button>
		</div>

		<!-- Remove Product Confirmation Dialog -->
		<BaseConfirmDialog
			:show="!!productToRemove"
			title="Remove Product"
			:message="`Are you sure you want to remove <strong class='text-black'>${store.getProductName(productToRemove)}</strong>?`"
			confirmLabel="Remove"
			@confirm="handleConfirmRemoveProduct"
			@cancel="handleCancelRemoveProduct"
		/>

		<!-- Reset Products Confirmation Dialog -->
		<BaseConfirmDialog
			:show="showResetConfirmation"
			title="Reset Products"
			message="Are you sure you want to reset all products back to your original selection?"
			confirmLabel="Reset"
			@confirm="handleConfirmResetProducts"
			@cancel="handleCancelResetProducts"
		/>
	</div>
</template>

<style scoped>
/* Custom color for Luxxbox burgundy (exact match to #6f0000) */
.text-luxxbox-burgundy {
	color: #6f0000;
}

/* Gray section styling (exact match to design) */
.results-section-gray {
	background-color: #ececec;
	border-color: #e4e4e4;
}

/* Panel collapse styles (mobile only) */
@media (max-width: 767px) {
	/* Handle positioning - above panel */
	.toggle-handle {
		bottom: 100%;
	}
}
</style>
